import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Form, Formik } from 'formik';

import ShippingMethods from './ShippingMethods/ShippingMethods';
import FormikErrorsGtmLogger from 'components/Common/FormikErrorsGtmLogger/FormikErrorsGtmLogger';
import ButtonWithArrow from 'components/UI/Buttons/ButtonWithArrow/ButtonWithArrow';
import Loading from 'components/Common/Loading/Loading';
import GenericErrorMessage from './GenericErrorMessage/GenericErrorMessage';
import CheckoutSummaryAfter from '../CheckoutSummary/CheckoutSummaryAfter/CheckoutSummaryAfter';
import useCart from 'hooks/useCart';
import useDeliveryMethods from 'hooks/checkout/useDeliveryMethods';
import validationSchema from 'schemas/ShippingValidationSchema';
import { getUri } from 'services/generic';
import { pushGtmEvent } from 'services/gtm/gtmEvents';
import { ShippingFormType } from 'types/ShippingFormType';
import { Cart } from 'types/Cart';

import styles from './ShippingForm.module.scss';

export default function ShippingForm({ cart }: { cart: Cart }): JSX.Element {
  const { t } = useTranslation(['checkout']);
  const { updateShippingMethod } = useCart();
  const { defaultMethod } = useDeliveryMethods();
  const navigate = useNavigate();
  const [isLoading, setLoading] = useState<boolean>(false);
  const [hasGenericError, setGenericError] = useState<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const initFormValues = {
    shippingMethod: cart.deliveryMethod?.delivery || defaultMethod || '',
    addressType: cart.deliveryMethod?.typeDeliveryAddress || '',
    relayPoint: cart.deliveryMethod?.relayPoint || null,
    mondialRelayPoint: cart.deliveryMethod?.mondialRelayPoint || null,
  };

  useEffect(() => {
    const pageFooterEl = document.querySelector('.page-footer');

    pageFooterEl?.classList.add(styles.footerCheckout);

    return () => {
      pageFooterEl?.classList.remove(styles.footerCheckout);
    };
  }, []);

  // Add the abort controller process
  const setShippingMethod = useCallback(
    async (params: Partial<ShippingFormType>): Promise<Cart | undefined> => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      return updateShippingMethod(params, {
        signal: abortController.signal,
      }).catch(error => {
        if (error.name === 'AbortError') return;

        console.error(error);

        return Promise.reject(error);
      });
    },
    []
  );

  function submitHandler(values: ShippingFormType) {
    setLoading(true);
    setGenericError(false);
    setShippingMethod({
      shippingMethod: values.shippingMethod,
      relayPoint: values.relayPoint || undefined,
      mondialRelayPoint: values.mondialRelayPoint || undefined,
      addressType: values.addressType,
    })
      .then(() => navigate(getUri('/checkout/payment')))
      .catch(() => setGenericError(true))
      .finally(() => setLoading(false));
  }

  function SubmitButton({
    onClick,
    className,
    disabled,
  }: {
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    className?: string;
    disabled?: boolean;
  } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
    return (
      <ButtonWithArrow
        type={'submit'}
        onClick={e => {
          pushGtmEvent({
            event: '_click_other',
            category: 'clic autre - checkout',
            element: 'validation livraison',
          });

          onClick && onClick(e);
        }}
        className={classNames(styles.submitButton, className)}
        disabled={disabled}
      >
        {t('SHIPPING_SUBMIT_BUTTON')}
      </ButtonWithArrow>
    );
  }

  function SubmitButtonFixed(props: {
    onClick: () => void;
    disabled: boolean;
  }) {
    return createPortal(
      <SubmitButton {...props} className={styles.fixedSubmitButton} />,
      document.body
    );
  }

  function SummarySubmitButton(props: {
    onClick: () => void;
    disabled: boolean;
  }): JSX.Element {
    return (
      <CheckoutSummaryAfter>
        <div className={styles.summaryButtonWrapper}>
          <SubmitButton {...props} />
        </div>
      </CheckoutSummaryAfter>
    );
  }

  return (
    <>
      {hasGenericError && <GenericErrorMessage />}
      <Formik
        initialValues={initFormValues}
        validationSchema={validationSchema(t)}
        onSubmit={submitHandler}
        enableReinitialize
        validateOnMount
      >
        {({ handleSubmit, isValid }) => (
          <Form>
            <FormikErrorsGtmLogger
              page_type={'livraison'}
              form_type={'mon mode de livraison'}
            />
            <ShippingMethods
              cart={cart}
              className={styles.methods}
              setShippingInformation={setShippingMethod}
            />
            <SummarySubmitButton
              onClick={() => handleSubmit()}
              disabled={!isValid}
            />
            <SubmitButtonFixed
              onClick={() => handleSubmit()}
              disabled={!isValid}
            />
          </Form>
        )}
      </Formik>
      {isLoading && <Loading loading />}
    </>
  );
}
